import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// ─── JSON-LD ──────────────────────────────────────────────────

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Mercúrio retrógrado realmente afeta a comunicação?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Mercúrio retrógrado é um fenômeno astronômico real, mas seu impacto é simbólico e psicológico, não mecânico. Durante esses períodos, muitas pessoas relatam falhas de comunicação, contratempos tecnológicos e revisões necessárias, o que pode ser um convite para revisar, refletir e desacelerar, em vez de lançar projetos novos. O efeito varia muito dependendo de como Mercúrio interage com o seu mapa natal pessoal.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quantas vezes por ano Mercúrio fica retrógrado?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Mercúrio fica retrógrado aproximadamente três vezes por ano, cada período durando de 3 a 4 semanas. Além do período retrógrado em si, existem as "sombras" (pré-retrogradação e pós-retrogradação) que podem durar mais algumas semanas cada, totalizando cerca de 9 a 12 semanas por ano com influência mercurial mais lenta.',
      },
    },
    {
      '@type': 'Question',
      name: 'Os retrógrados no mapa natal têm significado?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Sim. Planetas retrógrados no momento do nascimento indicam energias que são "interiorizadas" ou vividas de forma não-convencional. Uma pessoa com Vênus retrógrado natal, por exemplo, pode ter uma relação mais complexa com amor e autoestima, precisando de mais tempo para integrar essas qualidades internamente antes de expressá-las. Esses planetas natais retrógrados se tornam focos importantes de amadurecimento ao longo da vida.',
      },
    },
    {
      '@type': 'Question',
      name: 'Todos os planetas ficam retrógrados?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Quase todos. O Sol e a Lua nunca ficam retrógrados. Mercúrio, Vênus e Marte ficam retrógrados periodicamente a partir da perspectiva da Terra. Os planetas externos, Júpiter, Saturno, Urano, Netuno e Plutão, ficam retrógrados por meses a cada ano, pois se movem mais devagar. Quanto mais lento o planeta, mais tempo ele passa em retrogradação.',
      },
    },
  ],
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://professya.com' },
    { '@type': 'ListItem', position: 2, name: 'Retrógrado', item: 'https://professya.com/retrogrado' },
  ],
};

// ─── Planets data ─────────────────────────────────────────────

const RETROGRADE_PLANETS = [
  { name: 'Mercúrio', glyph: '☿', freq: '~3x/ano', duration: '3–4 semanas', theme: 'Comunicação, contratos, tecnologia, viagens curtas' },
  { name: 'Vênus', glyph: '♀', freq: '~1x/18 meses', duration: '6–7 semanas', theme: 'Amor, valores, finanças, estética e autoestima' },
  { name: 'Marte', glyph: '♂', freq: '~1x/2 anos', duration: '2–3 meses', theme: 'Ação, energia, conflitos, iniciativas e sexualidade' },
  { name: 'Júpiter', glyph: '♃', freq: 'Anual', duration: '~4 meses', theme: 'Crescimento, expansão, fé, filosofia e abundância' },
  { name: 'Saturno', glyph: '♄', freq: 'Anual', duration: '~4,5 meses', theme: 'Estrutura, responsabilidade, karma e limites' },
  { name: 'Urano', glyph: '♅', freq: 'Anual', duration: '~5 meses', theme: 'Inovação, ruptura, liberdade e mudança súbita' },
  { name: 'Netuno', glyph: '♆', freq: 'Anual', duration: '~5,5 meses', theme: 'Espiritualidade, ilusões, sonhos e dissolução' },
  { name: 'Plutão', glyph: '♇', freq: 'Anual', duration: '~5–6 meses', theme: 'Transformação profunda, poder e regeneração' },
];

// ─── Component ───────────────────────────────────────────────

export function RetrogradoPage() {
  return (
    <>
      <Helmet>
        <title>Mercúrio Retrógrado: O que é e Como Afeta você, Professya</title>
        <meta name="description" content="Entenda o que são planetas retrógrados, o que significa Mercúrio retrógrado e como a retrogradação afeta especificamente o seu mapa natal." />
        <meta property="og:title" content="Mercúrio Retrógrado: O que é e Como Afeta você, Professya" />
        <meta property="og:description" content="Entenda o que são planetas retrógrados, o que significa Mercúrio retrógrado e como a retrogradação afeta especificamente o seu mapa natal." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://professya.com/retrogrado" />
        <link rel="canonical" href="https://professya.com/retrogrado" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)' }}>

        {/* Hero */}
        <div style={{ background: 'linear-gradient(180deg, rgba(245,200,66,0.08) 0%, transparent 100%)', padding: '3rem 1.5rem 2rem', textAlign: 'center' }}>
          <Link to="/" style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', textDecoration: 'none', display: 'inline-block', marginBottom: '1.5rem' }}>
            ✦ Professya
          </Link>
          <div style={{ fontSize: '3.5rem', marginBottom: '0.75rem' }}>↩️</div>
          <h1 className="font-display" style={{ fontSize: 'clamp(2rem,6vw,3rem)', color: 'var(--text-primary)', marginBottom: '0.75rem' }}>
            Planetas Retrógrados: Mito e Realidade
          </h1>
          <p style={{ color: 'var(--text-secondary)', maxWidth: '640px', margin: '0 auto', lineHeight: 1.7, fontSize: 'var(--text-body)' }}>
            O que é retrogradação, por que Mercúrio retrógrado ficou famoso e como os períodos retrógrados afetam, ou não, o seu dia a dia e mapa natal.
          </p>
        </div>

        <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 1.5rem 4rem' }}>

          {/* Section 1, O que é retrogradação */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '0.875rem', color: 'var(--petrol)' }}>
              O que é retrogradação
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.85, marginBottom: '1rem' }}>
              Retrogradação é um fenômeno <strong style={{ color: 'var(--text-primary)' }}>ótico</strong>, não físico. Os planetas nunca andam literalmente para trás, o que ocorre é uma ilusão de perspectiva. Quando a Terra, em sua órbita mais rápida ao redor do Sol, ultrapassa um planeta mais lento (ou quando um planeta interno nos ultrapassa), a partir do nosso ponto de vista, esse planeta parece se mover em direção contrária à sua trajetória normal. É o mesmo efeito visual de ultrapassar um carro mais lento na rodovia: por um momento, o carro parece se mover para trás em relação a você.
            </p>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.85 }}>
              Na astrologia, esse movimento retrógrado é interpretado simbolicamente como uma energia que "volta para dentro", menos expressiva externamente, mais reflexiva e voltada para revisão. Os temas do planeta em questão pedem revisão, reprocessamento e introspecção durante esses períodos.
            </p>
          </section>

          {/* Section 2, Mercúrio retrógrado desmistificado */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '0.875rem' }}>
              Mercúrio retrógrado: desmistificado
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.85, marginBottom: '1rem' }}>
              Mercúrio é o planeta da comunicação, do transporte, da tecnologia, dos contratos e do pensamento lógico. Quando fica retrógrado, o que acontece cerca de três vezes por ano, é comum ouvir que "tudo dá errado": e-mails se perdem, carros quebram, mal-entendidos surgem. A verdade é mais sutil: o problema não é o planeta, mas a nossa tendência de forçar situações novas num período que pede revisão.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
              <div className="card" style={{ borderLeft: '3px solid #ef4444' }}>
                <h3 style={{ fontSize: 'var(--text-label)', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>Evite durante o retrógrado</h3>
                <ul style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', lineHeight: 1.8, paddingLeft: '1.25rem', margin: 0 }}>
                  <li>Assinar contratos importantes</li>
                  <li>Lançar produtos ou projetos novos</li>
                  <li>Comprar eletrônicos de alto valor</li>
                  <li>Ter conversas definitivas de relacionamento</li>
                </ul>
              </div>
              <div className="card" style={{ borderLeft: '3px solid var(--petrol)' }}>
                <h3 style={{ fontSize: 'var(--text-label)', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>Faça durante o retrógrado</h3>
                <ul style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', lineHeight: 1.8, paddingLeft: '1.25rem', margin: 0 }}>
                  <li>Revisar, editar e corrigir</li>
                  <li>Retomar projetos pausados</li>
                  <li>Reconectar-se com pessoas do passado</li>
                  <li>Refletir sobre comunicações importantes</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 3, Como afeta o mapa */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '0.875rem' }}>
              Como os retrógrados afetam seu mapa natal
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.85, marginBottom: '1rem' }}>
              O impacto de um planeta retrógrado em trânsito depende fortemente de onde esse planeta se encontra em relação ao seu mapa natal. Um Mercúrio retrógrado passando pela sua Casa 7 (relacionamentos) afeta áreas muito diferentes de um que transita pela Casa 10 (carreira). Mais ainda: se o trânsito retrógrado conjunciona um planeta natal importante, digamos, o seu Sol ou Vênus natal, o impacto será muito mais pessoal e perceptível do que para a maioria das pessoas.
            </p>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.85 }}>
              Além disso, pessoas que nasceram com planetas retrógrados no mapa natal têm uma relação diferente com esses planetas. Um Saturno retrógrado natal, por exemplo, pode indicar que a pessoa internaliza mais as questões de responsabilidade e estrutura, desenvolvendo-as "de dentro para fora", muitas vezes tornando-se extremamente capaz nessas áreas após um processo de amadurecimento mais lento e profundo.
            </p>
          </section>

          {/* Section 4, Tabela de planetas */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '1rem' }}>
              Planetas que ficam retrógrados e por quê
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {RETROGRADE_PLANETS.map(p => (
                <div key={p.name} className="card" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '0.75rem 1rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '1.5rem', color: 'var(--petrol)' }}>{p.glyph}</span>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 'var(--text-label)', margin: '0 0 0.2rem' }}>{p.name}</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>{p.theme}</p>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', margin: '0 0 0.1rem', fontWeight: 500 }}>{p.freq}</p>
                    <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>{p.duration}</p>
                  </div>
                </div>
              ))}
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', marginTop: '0.75rem', fontStyle: 'italic' }}>
              * O Sol e a Lua nunca ficam retrógrados a partir da perspectiva geocêntrica usada na astrologia ocidental.
            </p>
          </section>

          {/* FAQ */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '1.25rem' }}>
              Perguntas frequentes sobre retrógrados
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {faqSchema.mainEntity.map((item, i) => (
                <details key={i} className="card" style={{ cursor: 'pointer' }}>
                  <summary style={{ fontWeight: 600, fontSize: 'var(--text-label)', color: 'var(--text-primary)', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                    {item.name}
                    <span style={{ color: 'var(--petrol)', flexShrink: 0 }}>+</span>
                  </summary>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginTop: '0.75rem', fontSize: 'var(--text-label)' }}>
                    {item.acceptedAnswer.text}
                  </p>
                </details>
              ))}
            </div>
          </section>

          {/* CTA */}
          <div className="card" style={{ textAlign: 'center', background: 'linear-gradient(135deg, rgba(245,200,66,0.06) 0%, transparent 100%)', border: '1px solid rgba(245,200,66,0.2)' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>↩️</div>
            <p className="font-display" style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
              Quer saber como os retrógrados afetam o seu mapa?
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1.25rem' }}>
              Descubra quais planetas estão retrógrados no seu mapa natal e como os trânsitos atuais interagem com ele.
            </p>
            <Link to="/register" className="btn btn-primary">
              Ver como os retrógrados afetam meu mapa →
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
